import React from 'react';
import type { SvgIconComponent } from '@mui/icons-material';
import {
  AssignmentTurnedInOutlined as AssignmentTurnedInOutlinedIcon,
  ConstructionOutlined as ConstructionOutlinedIcon,
  DesignServicesOutlined as DesignServicesOutlinedIcon,
  FactCheckOutlined as FactCheckOutlinedIcon,
  SearchOutlined as SearchOutlinedIcon,
  VpnKeyOutlined as VpnKeyOutlinedIcon,
} from '@mui/icons-material';
import { useRevealOnScroll } from '../../hooks/useRevealOnScroll';
import { useSection5Image } from '../../hooks/useSection5Image';

interface MethodStep {
  step: number;
  title: string;
  description: string;
  icon: SvgIconComponent;
}

const METHOD_STEPS: MethodStep[] = [
  {
    step: 1,
    title: 'Étude de faisabilité',
    description: 'Analyse du potentiel et des contraintes',
    icon: AssignmentTurnedInOutlinedIcon,
  },
  {
    step: 2,
    title: 'Acquisition',
    description: 'Négociation et acquisition du terrain',
    icon: SearchOutlinedIcon,
  },
  {
    step: 3,
    title: 'Conception',
    description: 'Programmation et design architectural',
    icon: DesignServicesOutlinedIcon,
  },
  {
    step: 4,
    title: 'Autorisations',
    description: 'Démarches administratives et permis',
    icon: FactCheckOutlinedIcon,
  },
  {
    step: 5,
    title: 'Construction',
    description: 'Réalisation et suivi des travaux',
    icon: ConstructionOutlinedIcon,
  },
  {
    step: 6,
    title: 'Livraison',
    description: 'Remise des clés et accompagnement',
    icon: VpnKeyOutlinedIcon,
  },
];

interface MethodSectionProps {
  id?: string;
  className?: string;
}

const MethodSection: React.FC<MethodSectionProps> = ({ id = 'methode', className = '' }) => {
  const { ref: headerRef, visible: headerVisible } = useRevealOnScroll<HTMLElement>();
  const { image: backgroundImage } = useSection5Image();

  return (
    <section
      id={id}
      className={`relative scroll-mt-28 overflow-hidden bg-brand-charcoal py-12 md:scroll-mt-32 md:py-14 lg:py-16 ${className}`}
    >
      <img
        src={backgroundImage}
        alt=""
        className="pointer-events-none absolute inset-0 h-full w-full object-cover object-center"
        aria-hidden
        loading="lazy"
      />

      <div className="relative z-[1] mx-auto w-full max-w-[1440px] px-4 sm:px-5 md:px-6">
        <header
          ref={headerRef}
          className={`reveal-up mb-10 md:mb-12 lg:mb-14 ${headerVisible ? 'reveal-up--visible' : ''}`}
        >
          <p className="eyebrow-accent mb-3">04 - Méthode</p>
          <div
            className={`accent-dash mt-3 reveal-line ${headerVisible ? 'reveal-line--visible' : ''}`}
            aria-hidden
          />
          <h2 className="headline-brand-section mt-6 max-w-3xl text-white">
            Comment mène-t-on un projet avec{' '}
            <span className="headline-brand-accent">Proméa ?</span>
          </h2>
        </header>

        <ol
          className={`reveal-stagger grid grid-cols-1 gap-10 sm:grid-cols-2 sm:gap-y-12 lg:grid-cols-6 lg:gap-0 ${
            headerVisible ? 'reveal-stagger--active' : ''
          }`}
        >
          {METHOD_STEPS.map(({ step, title, description, icon: Icon }, index) => (
            <li
              key={step}
              className="group relative flex cursor-pointer flex-col items-center px-2 text-center sm:px-4 lg:px-3 xl:px-4"
            >
              {index > 0 ? (
                <span
                  className={`method-phase-divider ${
                    index % 2 === 1 ? 'sm:block' : 'sm:hidden'
                  } lg:block`}
                  aria-hidden
                />
              ) : null}

              <span
                className="mb-5 inline-flex h-11 w-11 items-center justify-center rounded-full bg-orange-500 text-[15px] font-semibold tracking-wide text-white"
                aria-hidden
              >
                {String(step).padStart(2, '0')}
              </span>

              <Icon className="mb-4 h-9 w-9 text-orange-500" aria-hidden />

              <h3 className="text-[15px] font-semibold leading-snug text-white motion-safe:transition-colors motion-safe:duration-300 motion-safe:group-hover:text-orange-400 sm:text-base lg:text-[17px]">
                {title}
              </h3>

              <p className="method-phase-copy mt-2.5 max-w-[11rem] text-[12px] font-normal leading-[1.55] text-white/75 motion-safe:transition-colors motion-safe:duration-300 motion-safe:group-hover:text-white sm:text-[13px] lg:mt-3">
                {description}
              </p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};

export default MethodSection;
